import { useState } from 'react';
import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { TrialAnchor, VoiceAnchor } from '@trinity/chat';

interface EvidencePopoverProps {
  trialAnchors: TrialAnchor[];
  voiceAnchors: VoiceAnchor[];
  variant?: 'icon' | 'button';
}

export function EvidencePopover({
  trialAnchors,
  voiceAnchors,
  variant = 'icon',
}: EvidencePopoverProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const hasEvidence = trialAnchors.length > 0 || voiceAnchors.length > 0;

  return (
    <>
      {variant === 'button' ? (
        <ViewEvidenceButton disabled={!hasEvidence} onClick={(e) => setAnchorEl(e.currentTarget)}>
          View Evidence
        </ViewEvidenceButton>
      ) : (
        <EvidenceIconButton
          disabled={!hasEvidence}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          title="View Evidence"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.414a1 1 0 0 0-.293-.707l-3.414-3.414A1 1 0 0 0 9.586 1H4zm0 1h5v3a1 1 0 0 0 1 1h3v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm6 .414L12.586 5H10V2.414zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1H5z" />
          </svg>
        </EvidenceIconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              maxWidth: 420,
              maxHeight: 400,
              overflow: 'auto',
            },
          },
        }}
      >
        <PopoverContent>
          {trialAnchors.length > 0 && (
            <>
              <SectionHeader>
                <SectionIcon>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.414a1 1 0 0 0-.293-.707l-3.414-3.414A1 1 0 0 0 9.586 1H4zm0 1h5v3a1 1 0 0 0 1 1h3v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                  </svg>
                </SectionIcon>
                <SectionTitle>Clinical Trial Evidence</SectionTitle>
              </SectionHeader>
              <Divider />
              <SectionBody>
                {trialAnchors.map((anchor, i) => (
                  <EvidenceItem key={i}>
                    <EvidenceQuote>{anchor.claimText}</EvidenceQuote>
                    <EvidenceMeta>
                      {anchor.studyId} · Phase {anchor.studyPhase} · {anchor.category}
                    </EvidenceMeta>
                  </EvidenceItem>
                ))}
              </SectionBody>
            </>
          )}
          {voiceAnchors.length > 0 && (
            <>
              <SectionHeader $mt={trialAnchors.length > 0}>
                <SectionIcon>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a4 4 0 0 0-4 4v3a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4zM5 5a3 3 0 0 1 6 0v3a3 3 0 0 1-6 0V5zm-2.5 3a.5.5 0 0 1 .5.5A5 5 0 0 0 7.5 13.5v1a.5.5 0 0 1-1 0v-1.05A6 6 0 0 1 2 8.5a.5.5 0 0 1 .5-.5zm11 0a.5.5 0 0 1 .5.5A6 6 0 0 1 9.5 13.45v1.05a.5.5 0 0 1-1 0v-1A5 5 0 0 0 13 8.5a.5.5 0 0 1 .5-.5z" />
                  </svg>
                </SectionIcon>
                <SectionTitle>Voice of Customer</SectionTitle>
              </SectionHeader>
              <Divider />
              <SectionBody>
                {voiceAnchors.map((anchor, i) => (
                  <EvidenceItem key={i}>
                    <EvidenceQuote>&ldquo;{anchor.quoteText}&rdquo;</EvidenceQuote>
                    <EvidenceMeta>
                      {anchor.stakeholder} · {anchor.construct}
                    </EvidenceMeta>
                  </EvidenceItem>
                ))}
              </SectionBody>
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const EvidenceIconButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: 6,
  background: '#f0f0f0',
  color: '#666',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover:not(:disabled)': {
    background: '#e0e0e0',
    color: '#565add',
  },
  '&:disabled': {
    opacity: 0.35,
    cursor: 'default',
  },
});

const ViewEvidenceButton = styled('button')({
  padding: '4px 12px',
  border: '1px solid #d0d0d0',
  borderRadius: 6,
  background: 'white',
  color: '#555',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover:not(:disabled)': {
    background: '#f0f0f0',
    color: '#1271c4',
    borderColor: '#1271c4',
  },
  '&:disabled': {
    opacity: 0.35,
    cursor: 'default',
  },
});

const PopoverContent = styled('div')({
  minWidth: 300,
});

const SectionHeader = styled('div')<{ $mt?: boolean }>(({ $mt }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  ...($mt ? { borderTop: '1px solid #e8e8e8' } : {}),
}));

const SectionIcon = styled('span')({
  display: 'flex',
  alignItems: 'center',
  color: '#565add',
});

const SectionTitle = styled('div')({
  fontSize: 14,
  fontWeight: 600,
  color: '#1a1a1a',
});

const Divider = styled('div')({
  height: 1,
  background: '#e8e8e8',
});

const SectionBody = styled('div')({
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const EvidenceItem = styled('div')({
  paddingLeft: 10,
  borderLeft: '2px solid #d1d5db',
});

const EvidenceQuote = styled('div')({
  fontSize: 12,
  color: '#333',
  lineHeight: 1.45,
  marginBottom: 2,
});

const EvidenceMeta = styled('div')({
  fontSize: 10,
  color: '#888',
});
